import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { FieldValues, UseFormRegister } from 'react-hook-form';

import { IHeaderDictionary } from '@/dictionaries/components/header/types';
import HeaderSelect from './HeaderSelect';
import MenuOptions from './MenuOptions';

import Button from '../Button';
import { ContactUsModalContent } from '../ContactUsModalContent';
import Input from '../Input';
import LoginModal from '../LoginModal';
import ShortListModalContent from '@/components/ShortListModalContent';

import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import { IModalsDictionary } from '@/dictionaries/components/modals/types';
import { ISendToFriendsFormDictionary } from '@/dictionaries/components/SendToFriendsForm/types';
import { IContactPageDictionary } from '@/dictionaries/pages/contacts/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import { ISlugDictionary } from '@/dictionaries/utils/slugs/types';
import { IURLDictionary } from '@/dictionaries/utils/url/types';
import { useChat } from '@/hooks/useChatContext';
import useGlobalContext from '@/hooks/useGlobalContext';
import useUserContext from '@/hooks/useUserContext';
import { cn } from '@/lib/utils';
import { IoChatboxOutline, IoSearchSharp } from 'react-icons/io5';
import { Locale } from '../../../i18n.config';
import logoBlue from '../../../public/logo-desktop.svg';
import logoWhite from '../../../public/white-desktop-logo.svg';
import UserAvatar from '../UserAvatar';
import Link from 'next/link';

interface HeaderPortraitProps {
  dict: IHeaderDictionary;
  urlDict: IURLDictionary;
  modalsDict: IModalsDictionary;
  enquiryFormDict: IEnquiryFormDictionary;
  sendToFriendFormDict: ISendToFriendsFormDictionary;
  contactUsDict: IContactPageDictionary;
  countriesDict: ICountriesDictionary;
  datePickerDict: IDatePickerDictonary;
  slugsDict: ISlugDictionary;
  setShowVilla: Dispatch<SetStateAction<boolean>>;
  setShowGuide: Dispatch<SetStateAction<boolean>>;
  handleSearch: () => void;
  register: UseFormRegister<FieldValues>;
  lang: Locale;
  isActive: boolean;
  openMenu: boolean;
  setOpenMenu: Dispatch<SetStateAction<boolean>>;
}

const HeaderPortrait: FC<HeaderPortraitProps> = ({
  dict,
  urlDict,
  modalsDict,
  setShowVilla,
  setShowGuide,
  enquiryFormDict,
  sendToFriendFormDict,
  countriesDict,
  contactUsDict,
  datePickerDict,
  slugsDict,
  handleSearch,
  register,
  lang,
  isActive,
  openMenu,
  setOpenMenu,
}) => {
  const { isLogged, userGrant, user } = useUserContext();
  const { isHeaderActive, setIsHeaderActive } = useGlobalContext();

  const bgMenu = openMenu ? 'bg-primary' : 'bg-white';

  const pathname = usePathname();

  const handleSetOpenMenu = () => {
    setOpenMenu(!openMenu);
    setIsHeaderActive(!isHeaderActive);
  };

  useEffect(() => {
    setOpenMenu(false);
  }, [pathname, setOpenMenu]);

  return (
    <div
      className={cn(
        `w-full h-full desktop:hidden flex flex-col z-[999]`,
        isHeaderActive && 'bg-white',
      )}>
      <div className="flex items-center justify-between h-[100px] px-[15px] sm:px-10">
        <div className="flex items-center justify-between flex-1">
          <button
            id="menu-button"
            aria-label="Open menu"
            className="relative w-[27px] h-[20px] ml-[8px]"
            onClick={handleSetOpenMenu}>
            <span
              className={cn(
                `absolute top-0 left-0 h-[2px] w-full`,
                isActive ? 'bg-black' : bgMenu,
              )}></span>
            <span
              className={cn(
                `absolute top-[45%] left-0 h-[2px] w-full`,
                isActive ? 'bg-black' : bgMenu,
              )}></span>
            <span
              className={cn(
                `absolute bottom-0 left-0 h-[2px] w-full`,
                isActive ? 'bg-black' : bgMenu,
              )}></span>
          </button>

          <Link href={lang == 'en' ? '/' : `/${lang}`}>
            <Image
              priority={true}
              src={isActive ? logoBlue : logoWhite}
              alt="logo"
              width={225}
              height={40}
            />
          </Link>

          <ul className="flex justify-end">
            {isLogged && (
              <li className="px-[8px] relative">
                <UserAvatar.Container
                  lang={lang}
                  withAnchor
                  userGrant={userGrant}>
                  <UserAvatar.Root userGrant={userGrant} user={user} />
                </UserAvatar.Container>
              </li>
            )}
            <li className="px-[8px]">
              <ShortListModalContent
                lang={lang}
                enquiryFormDict={enquiryFormDict}
                sendToFriendFormDict={sendToFriendFormDict}
                countriesDict={countriesDict}
                datePickerDict={datePickerDict}
                dict={modalsDict}
              />
            </li>
          </ul>
        </div>
      </div>
      {openMenu && (
        <div className="absolute flex flex-col top-[100px] left-0 bg-white shadow-[0_4px_5px_0_rgba(0,0,0,0.1)] px-4 pb-4 w-full gap-[20px] z-[999]">
          <MenuOptions
            dict={dict}
            lang={lang}
            pathname={pathname}
            setShowVilla={setShowVilla}
            setShowGuide={setShowGuide}
          />

          <div>
            {!isLogged && <LoginModal lang={lang} dict={modalsDict} isHeader />}
          </div>

          <HeaderSelect
            dict={dict}
            urlDict={urlDict}
            slugsDict={slugsDict}
            lang={lang}
            countriesDict={countriesDict}
            contactUsDict={contactUsDict}
            phone={dict.nav.phone}
            modalsDict={modalsDict}
            enquiryFormDict={enquiryFormDict}
            sendToFriendFormDict={sendToFriendFormDict}
            datePickerDict={datePickerDict}
            isActive={isActive}
          />

          <form
            className="flex"
            onSubmit={() => {
              handleSearch();
              setOpenMenu(false);
            }}>
            <Input.Container className="grow">
              <Input.Root
                register={register}
                name="search"
                placeholder={dict.search}
                className="placeholder:text-gray-400 placeholder:text-sm"
              />
            </Input.Container>
            <Button className="w-auto max-h-[44px] min-h-[44px] px-[10px] py-[15px] ml-4">
              <span className="text-lg m-[5px]">
                <IoSearchSharp />
              </span>
            </Button>
          </form>
        </div>
      )}
    </div>
  );
};

export default HeaderPortrait;
